import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "./utils/api";

// 1. Imports des Contextes et Hooks
// Note: ThemeProvider est rendu ici, mais useTheme() doit être dans un enfant
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { SelectedProvider, useSelected } from "./contexts/SelectedContext";
import { NotificationProvider, useNotification } from "./contexts/NotificationContext";

// 2. Imports des Composants et Pages
import LandingPage from "./pages/LandingPage";
import { MENTIONS_LEGALES_TEXT } from "./pages/legal/LegalContent";
import { CONDITIONS_GENERALES_VENTES } from "./pages/legal/CGVContent"
import { POLITIQUE_CONFIDENTIALITE } from "./pages/legal/ConfidentialiteContent";
import LegalPage from "./pages/LegalPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AccountsPage from "./pages/AccountsPage";
import RecurrencePage from "./pages/RecurrencePage";
import DebtCreditPage from './pages/DebtCreditPage';
import JoinAccountPage from './pages/JoinAccountPage';
import VerificationStatusPage from './pages/VerificationStatusPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SettingsPage from "./pages/SettingsPage";
import BillingPage from './pages/BillingPage';
import LoadingPage from "./pages/LoadingPage";
import SituationPage from "./pages/SituationPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import Topbar from "./components/Topbar";
import Toast from './components/Toast';

import useSubscriptionStatus from './hooks/useSubscriptionStatus';

// Imports des images
import BackgroundImageBlue from './images/background_blue.png';
import BackgroundImageGreen from './images/background_green.png';
import BackgroundImageRed from './images/background_red.png';
import BackgroundImageOrange from './images/background_orange.png';
import BackgroundImageYellow from './images/background_yellow.png';

// Composant d'enveloppement pour la protection
const ProtectedRoute = ({ children }) => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const { isExpired } = useSubscriptionStatus(); 

    // 💡 On vérifie juste si l'objet user existe. 
    // Si le cookie est mort, authFetch (dans refreshAllData) renverra 401 et déconnectera l'utilisateur.
    if (!currentUser) {
        return <Navigate to="/" replace />;
    }
    
    if (isExpired) {
        return <Navigate to="/billing" replace />;
    }

    return children;
};

// --- Composant Contenu de l'Application (où les hooks sont valides) ---
function AppContent() {
    const { notify } = useNotification();
    const [adminRequest, setAdminRequest] = useState(false);
    const { currentTheme } = useTheme();
    const { 
        setSelectedPerson, setSelectedAccount, 
        selectedPerson, selectedAccount 
    } = useSelected();

    const navigate = useNavigate();
    const location = useLocation();

    // --- États ---
    const [currentUser, setCurrentUser] = useState(null);
    const [persons, setPersons] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
    const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const adminSecretPath = currentUser?.adminPath || "protected-default-path";
    
    // --- LOGIQUE DE DÉCONNEXION ---
    const handleLogout = useCallback(async () => {
        // 💡 SI ON EST EN MODE ASSISTANCE
        if (currentUser?.adminMode) {
            try {
                // 1. On demande au backend de restaurer la session admin
                const res = await authFetch.post("/api/admin/exit-assistance");
                if (res.ok) {
                    const data = await res.json();
                    // 2. On restaure le profil Admin original
                    setCurrentUser(data.adminUser);
                    localStorage.setItem('user', JSON.stringify(data.adminUser));
                    
                    // 3. On redirige vers la page Admin
                    const adminPath = `/admin-${data.adminUser.adminPath}`;
                    window.location.href = adminPath;
                    return; // On arrête là, pas de déconnexion totale
                }
            } catch (err) {
                console.error("Erreur sortie assistance", err);
            }
        }

        // 💡 DÉCONNEXION NORMALE (si pas adminMode ou si erreur au dessus)
        try {
            await authFetch.post("/logout");
        } catch (err) {
            console.error("Erreur logout", err);
        } finally {
            setCurrentUser(null);
            localStorage.removeItem('user'); 
            localStorage.removeItem('selectedPerson');
            localStorage.removeItem('selectedAccount');
            navigate("/login");
        }
    }, [currentUser, navigate]);


    const handleLogin = useCallback((user) => {
        const storedData = localStorage.getItem('user');
        const userData = storedData ? JSON.parse(storedData) : user;
        
        setCurrentUser(userData);
        
        // 💡 Redirection stricte selon le rôle
        if (userData.role === 'Admin') {
            navigate("/admin", { replace: true });
        } else {
            navigate("/comptes", { replace: true });
        }
    }, [navigate]);

    // --- LOGIQUE DE RECHARGEMENT DES DONNÉES ---
    const refreshAllData = useCallback(async () => {
        if (!currentUser) return;

        // 💡 Si on est en adminMode, currentUser.id EST l'id du client (car on a écrasé l'objet user)
        // On s'assure de bien prendre l'ID de la session active
        const idToFetch = currentUser.id; 

        try {
            const response = await authFetch.get(`/bootstrap-data/${idToFetch}`);
            
            if (response.status === 401) throw new Error("Session expirée.");
            
            // Si le serveur dit 403 alors qu'on est admin, c'est qu'il y a un souci de synchro
            if (response.status === 403 && !currentUser.adminMode) {
                handleLogout();
                return;
            }

            const data = await response.json();
            
            // 1. Mise à jour des Personnes
            setPersons(data.persons);
            if (selectedPerson && !data.persons.find(p => p.id === selectedPerson.id)) {
                setSelectedPerson(null);
                setSelectedAccount(null);
            }

            // 2. Mise à jour des Catégories et Comptes
            setCategories(data.categories);
            setAllCategories(data.allCategories);
            setAccounts(data.accounts);
            if (selectedAccount && !data.accounts.find(acc => acc.id === selectedAccount.id)) {
                setSelectedAccount(null);
            }

            // 3. Traitement des Années
            const minYear = Number(data.minYear);
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let year = minYear; year <= currentYear + 1; year++) {
                years.push(year);
            }
            setAvailableYears(years.sort((a, b) => b - a));

        } catch (err) {
            console.error("❌ Erreur de rafraîchissement (Bootstrap) :", err);
            if (err.message === "Session expirée.") handleLogout();
        }
    }, [currentUser, handleLogout, selectedPerson, selectedAccount, setSelectedPerson, setSelectedAccount]);

    // --- LOGIQUE DE CHARGEMENT INITIAL (AVEC SPLASH SCREEN ET DÉLAI) ---
    const loadInitialData = useCallback(async () => {
        if (!currentUser || initialDataLoaded) return; // Sécurité

        setIsLoadingData(true); // Active le loader
        
        // Le délai minimum pour l'effet visuel
        const minDelayPromise = new Promise(resolve => setTimeout(resolve, 2000)); 

        try {
            // Exécute le rafraîchissement rapide et attend le délai en parallèle
            await Promise.all([
                refreshAllData(), 
                minDelayPromise,
            ]);

            setInitialDataLoaded(true); // Marque le chargement initial comme terminé

        } catch (error) {
            console.error("Erreur critique lors du chargement initial:", error);
        } finally {
            setIsLoadingData(false); // Désactive le loader (ce qui affiche AccountsPage)
        }
    }, [currentUser, initialDataLoaded, refreshAllData]);

    // --- Fonction pour mettre à jour le profil utilisateur localement ---
    const updateCurrentUser = useCallback((updates) => {
        setCurrentUser(prev => {
            if (!prev) return null;
            const newUser = { ...prev, ...updates };
            // On met à jour le localStorage pour que les infos soient persistantes
            localStorage.setItem('user', JSON.stringify(newUser));
            return newUser;
        });
    }, []);

    // --- EFFECTS ---
    useEffect(() => {
        if (currentUser && !initialDataLoaded) {
            loadInitialData();
        }
    }, [currentUser, loadInitialData, initialDataLoaded]);

    useEffect(() => {
        if (!currentUser) {
            const storedData = localStorage.getItem('user');
            if (storedData) {
                try {
                    const userData = JSON.parse(storedData);
                    // 💡 Plus besoin de vérifier .token ici
                    setCurrentUser(userData);
                    
                    if (location.pathname === '/' || location.pathname === '/inscription') {
                        if (userData.role === 'Admin') {
                            navigate("/admin", { replace: true });
                        } else {
                            navigate("/comptes", { replace: true });
                        }
                    }
                } catch (error) {
                    localStorage.removeItem('user');
                }
            }
        }
    }, [location.pathname, navigate, currentUser]);

    useEffect(() => {
        // 1. Sécurité de base
        if (!currentUser || currentUser.role === 'Admin' || currentUser.adminMode) return;

        const checkRequest = async () => {
            if (!currentUser.support_access_expires) return;

            const now = new Date();
            const expiry = new Date(currentUser.support_access_expires);

            if (now > expiry) {
                // Si le temps est écoulé, on nettoie localement pour arrêter le polling
                updateCurrentUser({ support_access_expires: null });
                return;
            }

            try {
                const res = await authFetch.get("/api/user/check-admin-request");
                // Si l'utilisateur a fermé l'accès côté serveur entre temps (404 ou 403), on arrête
                if (!res.ok) return; 

                const data = await res.json();
                if (data.admin_access_status === 'pending') {
                    setAdminRequest(true);
                }
            } catch (e) { 
                console.error("Support Poll error", e); 
            }
        };

        // Lancer immédiatement une vérification si le support est actif
        if (currentUser.support_access_expires) {
            checkRequest();
        }

        // Créer l'intervalle (toutes les 5 secondes)
        const interval = setInterval(checkRequest, 5000);

        return () => clearInterval(interval);
    }, [currentUser, updateCurrentUser]);

    const handleAdminResponse = async (accept) => {
        try {
            // On masque immédiatement l'overlay pour éviter le spam de clics
            setAdminRequest(false); 

            const res = await authFetch.post("/api/user/respond-admin-request", { accept });
            
            if (res.ok) {
                if (accept) {
                    notify("Accès autorisé. L'administrateur va se connecter.", "success");
                } else {
                    notify("Demande refusée.", "info");
                }
            } else {
                // Si erreur serveur, on permet à l'overlay de revenir au prochain poll
                notify("Erreur lors de l'envoi de la réponse.", "error");
            }
        } catch (e) { 
            notify("Erreur réseau.", "error");
        }
    };

    const refreshDashboardData = () => {
        setDashboardRefreshKey(prev => prev + 1);
    };

    const routesWithoutTopbar = ['/', '/mentions-legales', '/confidentialiteContent', '/login', '/inscription', '/comptes', '/billing', '/join-account', '/verification-status', '/forgot-password', '/settings'];
    const showTopbar = currentUser && !routesWithoutTopbar.includes(location.pathname);

    // --- Logique de sélection de l'image de fond ---
    const backgroundImage = 
        currentTheme === 'green' ? BackgroundImageGreen : 
        currentTheme === 'red' ? BackgroundImageRed :
        currentTheme === 'orange' ? BackgroundImageOrange :
        currentTheme === 'yellow' ? BackgroundImageYellow :
        BackgroundImageBlue;

    return (
        <div className="App" style={{ ...styles.appContainer, backgroundImage: `url(${backgroundImage})` }}>
            {showTopbar && (
                <Topbar
                    currentUser={currentUser}
                    persons={persons}
                    accounts={accounts}
                    selectedYear={selectedYear}
                    setSelectedYear={setSelectedYear}
                    onLogout={handleLogout}
                    availableYears={availableYears}
                />
            )}

            <main style={{...styles.mainContent, paddingTop: showTopbar ? "60px" : "0px"}}>
                <Routes>
                    <Route path="/" element={<LandingPage onLogin={handleLogin} />} />
                    <Route path="/mentions-legales" element={<LegalPage title="Mentions Légales" content={MENTIONS_LEGALES_TEXT} />} />
                    <Route path="/cgv" element={<LegalPage title="Conditions Générales de Vente" content={CONDITIONS_GENERALES_VENTES} />} />
                    <Route path="/confidentialite" element={<LegalPage title="Politique de confidentialité" content={POLITIQUE_CONFIDENTIALITE} />} />
                    <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
                    <Route path="/inscription" element={<RegisterPage />} />
                    <Route path="/join-account" element={<JoinAccountPage />} />
                    <Route path="/verification-status" element={<VerificationStatusPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Route protégée : Comptes (page d'accueil après login) */}
                    <Route
                        path="/comptes"
                        element={
                            <ProtectedRoute>
                                {isLoadingData ? (
                                    <LoadingPage /> 
                                ) : (
                                    <AccountsPage
                                        currentUser={currentUser}
                                        persons={persons} // Les states de App.js sont initialisés à [], ce qui est sûr
                                        accounts={accounts}
                                        refreshAllData={refreshAllData}
                                        refreshDashboardData={refreshDashboardData}
                                    />
                                )}
                            </ProtectedRoute>
                        }
                    />

                    {/* Routes protégées qui nécessitent la sélection Personne/Compte */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardPage
                                    currentUser={currentUser}
                                    persons={persons}
                                    accounts={accounts}
                                    categories={allCategories}
                                    selectedYear={selectedYear}
                                    refreshAllData={refreshAllData}
                                    refreshDashboardData={refreshDashboardData}
                                    key={dashboardRefreshKey}
                                    dashboardRefreshKey={dashboardRefreshKey}
                                />
                            </ProtectedRoute>
                        }
                    />
                    {/* Route protégée : Situation / Budget Expert */}
                    <Route
                        path="/situation"
                        element={
                            <ProtectedRoute>
                                <SituationPage 
                                    selectedYear={selectedYear} 
                                />
                            </ProtectedRoute>
                        }
                    />
                    {/* ... (Autres routes protégées similaires pour Dettes et Recurrences) ... */}
                    <Route
                        path="/dettes"
                        element={
                            <ProtectedRoute>
                                <DebtCreditPage
                                    currentUser={currentUser}
                                    categories={categories}
                                    accounts={accounts}
                                    persons={persons}
                                    refreshAllData={refreshAllData}
                                    refreshDashboardData={refreshDashboardData}
                                />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/recurrence"
                        element={
                            <ProtectedRoute>
                                <RecurrencePage
                                    currentUser={currentUser}
                                    categories={categories}
                                    accounts={accounts}
                                    persons={persons}
                                    refreshAllData={refreshAllData}
                                    refreshDashboardData={refreshDashboardData}
                                    selectedPerson={selectedPerson}
                                    selectedAccount={selectedAccount}
                                />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/billing" element={<BillingPage />} />

                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <SettingsPage
                                  currentUser={currentUser}
                                  isUserAdmin={currentUser?.role === 'Admin'}
                                  updateCurrentUser={updateCurrentUser}
                                />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path={`/admin-${adminSecretPath}`}
                        element={
                            <ProtectedRoute>
                                <AdminUsersPage
                                    updateCurrentUser={updateCurrentUser}
                                />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </main>

            {adminRequest && (
                <div style={styles.overlay}>
                    <div style={{...styles.modal, backgroundColor: currentTheme === 'blue' ? '#fff' : '#f9f9f9'}}>
                        <h3 style={{marginTop: 0}}>🛠️ Demande d'assistance</h3>
                        <p>Un administrateur souhaite accéder temporairement à votre compte pour vous aider.</p>
                        <p><strong>L'administrateur pourra voir et modifier vos données pendant 1 heure.</strong></p>
                        <div style={{display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px'}}>
                            <button onClick={() => handleAdminResponse(false)} style={styles.btnRefuse}>Refuser</button>
                            <button onClick={() => handleAdminResponse(true)} style={styles.btnAccept}>Autoriser l'accès</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Styles (Inchngés) ---
const styles = {
    appContainer: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
    },
    mainContent: {
        flex: 1,
        paddingTop: "0px",
    },

    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
    },
    modal: {
        padding: '30px', borderRadius: '12px', maxWidth: '450px',
        textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
    },
    btnAccept: {
        padding: '10px 20px', border: 'none', borderRadius: '6px',
        backgroundColor: '#27ae60', color: 'white', fontWeight: 'bold', cursor: 'pointer'
    },
    btnRefuse: {
        padding: '10px 20px', border: 'none', borderRadius: '6px',
        backgroundColor: '#e74c3c', color: 'white', fontWeight: 'bold', cursor: 'pointer'
    }
};

// --- Composant Racine Final ---
export default function AppRouterWrapper() {
    return (
        <Router>
            <NotificationProvider>
                <ThemeProvider>
                    <SelectedProvider> 
                        <AppContent />
                    </SelectedProvider>
                    <Toast /> 
                </ThemeProvider>
            </NotificationProvider>
        </Router>
    );
}